import { action, set } from '@ember/object';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import type { Hotel, Roomtype } from 'frontend-utils/models';
import {
  HOTEL_COOKIE_NAME,
  OTA_IDS,
  type Platform,
  PLATFORMS,
} from 'frontend-utils/utils/constants/constant-values';
import type {
  Demand,
  Event,
  Holiday,
  Occupancy,
  Period,
  ScheduledShopPause,
  ShortTermRentalRate,
} from 'lumen/models';
import type { Transition } from 'lumen/services/transition';
import {
  APIGATEWAY,
  COMPARETYPES,
  type MealTypeValues,
  type MembershipType,
  type RateParam,
} from 'lumen/utils/constants/general-constants';
import { getPeriodsInRange } from 'lumen/utils/dates/moment-helpers';
import { refreshIfOtaIsDisconnected } from 'lumen/utils/disconnected-otas';
import { extendObj } from 'lumen/utils/extend-vanilla-js';
import { isComparingRates } from 'lumen/utils/is-comparing-rates';
import {
  mapCompareParamsToAPIParams,
  mapRateParamToAPIParams,
} from 'lumen/utils/rates-request';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';
import { hash } from 'rsvp';

const MAX_FUTURE_INTERNAL_LIVESHOP_DAYS = 90;

type RatesQueryParams = {
  compareType: string;
  compareValue: string;
  compsetId: number;
  highlight: string;
  los: number;
  maxPersons: number;
  mealPlan: MealTypeValues;
  membershipType: MembershipType;
  month: string;
  otaId: string;
  platform: Platform;
  rateChanges: string;
  rateParam: RateParam;
  roomType: string;
  view: string;
};

interface PeriodsResponseMeta {
  disconnected_otas: string[];
}

interface RentalResponseMeta {
  relevant_properties: number;
}

interface RatesRouteModel {
  periods: {
    data: Partial<Period>[];
    isLoading: boolean;
  };
  roomtypes: {
    data: Roomtype[];
    isLoading: boolean;
  };
}

export default class HotelRatesRoute extends Route {
  queryParams = {
    compareType: {
      refreshModel: true,
    },
    compareValue: {
      refreshModel: true,
    },
    compsetId: {
      refreshModel: true,
    },
    highlight: {
      refreshModel: false,
    },
    los: {
      refreshModel: true,
    },
    maxPersons: {
      refreshModel: true,
    },
    mealPlan: {
      refreshModel: true,
    },
    membershipType: {
      refreshModel: true,
    },
    month: {
      refreshModel: true,
    },
    otaId: {
      refreshModel: true,
    },
    platform: {
      refreshModel: true,
    },
    rateParam: {
      refreshModel: true,
    },
    roomType: {
      refreshModel: true,
    },
    view: {
      refreshModel: false,
    },
  };

  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare cookie: Services['cookie'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare liveupdate: Services['liveupdate'];
  @service declare rates: Services['rates'];
  @service declare store: Services['store'];
  @service declare tab: Services['tab'];
  @service declare transition: Services['transition'];
  @service declare usageAnalytics: Services['usage-analytics'];

  lastRoomtypeResponse: Roomtype[] | null = null;
  requestedOtaId: string | null = null;

  fetchScheduledShopPausesTask = task(
    { restartable: true },
    async ({ subscriptionId }: { subscriptionId: string }) => {
      try {
        const scheduledShopPauses = await this.store.query<ScheduledShopPause>(
          'scheduled-shop-pause',
          {
            namespace: APIGATEWAY,
            typeKey: 'shopping/scheduled_shop_pauses',
            subscription_id: subscriptionId,
          },
        );

        return [...scheduledShopPauses];
      } catch (error) {
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  activate(transition: Transition): void {
    super.activate(transition);
    set(this.liveupdate, 'ratesTabOpen', true);
  }

  beforeModel(transition: Transition): void {
    if (!this.tab.hasRateInsight) {
      this.transition.redirectToEnabledTab(transition);
    } else {
      this.transition.validateRatesQueryParams(transition);
    }
  }

  deactivate(): void {
    set(this.liveupdate, 'ratesTabOpen', false);

    this.rates.clearCachedLosRestrictionRates();

    /**
     * In application tests, the app gets torn down after each test. This causes the store to be destroyed
     * before the deactivate hook is called. This check is to prevent the store from being accessed after it's destroyed.
     */
    if (!this.store.isDestroyed) {
      // this.store.unloadAll('day');
      this.store.unloadAll('event');
      // this.store.unloadAll('period');
      this.store.unloadAll('roomtype');
      this.store.unloadAll('holiday');
      // this.store.unloadAll('occupancy');
      this.store.unloadAll('demand');
    }

    this.lastRoomtypeResponse = null;
  }

  getChangeDates(month: Dayjs): string[] {
    const today = this.hotelAndMarketService.getLocalTime();
    const previousMonth = today.subtract(1, 'month');
    const nextMonth = today.add(1, 'month');
    const monthAfterNextMonth = today.add(2, 'month');

    if (month.isSame(today, 'month') || month.isSame(previousMonth, 'month')) {
      return [today.subtract(1, 'day').format('YYYY-MM-DD')];
    }
    if (
      month.isSame(nextMonth, 'month') ||
      month.isSame(monthAfterNextMonth, 'month')
    ) {
      return [
        today.subtract(1, 'day').format('YYYY-MM-DD'),
        today.subtract(7, 'day').format('YYYY-MM-DD'),
      ];
    }
    if (month.isAfter(monthAfterNextMonth)) {
      return [today.subtract(7, 'day').format('YYYY-MM-DD')];
    }

    return [];
  }

  model(params: RatesQueryParams): RatesRouteModel {
    const model: RatesRouteModel = {
      periods: {
        isLoading: true,
        data: [],
      },
      roomtypes: {
        isLoading: true,
        data: [],
      },
    };

    // set month param in hotel controller to have the correct links in header
    // eslint-disable-next-line ember/no-controller-access-in-routes
    set(this.controllerFor('hotel'), 'month', params.month);

    const month = this.hotelAndMarketService.toLocalTime(`${params.month}-15`);
    const dateRangeStart = month.startOf('month').startOf('week');
    const dateRangeEnd = month.endOf('month').endOf('week').startOf('day');

    const changeDates = this.getChangeDates(month);
    const compareExtractDates: Record<string, string> = {};

    // Rate changes are only shown in Short-term rental tooltip
    if (
      this.access.hasShortTermRentalAccess &&
      !this.access.hasShortTermRentalOnlyStrTab
    ) {
      changeDates.forEach((changeDate, index) => {
        // compare_extract_date, compare2_extract_date, compare2_extract_date, ...
        compareExtractDates[
          `compare${index > 0 ? index + 1 : ''}_extract_date`
        ] = changeDate;
      });
    }

    const hotelId = (this.modelFor('hotel') as Hotel).id;

    const requestDays: Record<string, string | string[]> = {
      namespace: APIGATEWAY,
      from_date_range_start: dateRangeStart.format('YYYY-MM-DD'),
      from_date_range_end: dateRangeEnd.format('YYYY-MM-DD'),
      hotel_id: hotelId!,
    };

    let requestPeriods: Record<string, string | number | string[]> = {
      namespace: APIGATEWAY,
      typeKey: 'rates',
      ota: params.otaId,
      los: params.los,
      mealtype: params.mealPlan,
      persons: params.maxPersons,
      roomtype: params.roomType,
      membershiptype: params.membershipType,
      compset_ids: params.compsetId,
      platform: params.platform,
      meta: 'nested',
    };

    const isCompareActive = isComparingRates(
      params.compareType,
      params.compareValue,
    );

    // So the compare extract date is used for the STR tooltip, this conflucts with the compare_value if you do a date compare
    // So we need to only add the compare extarct date, when not comparing
    if (!isCompareActive) {
      requestPeriods = {
        ...requestPeriods,
        ...compareExtractDates,
      };
    }

    mapRateParamToAPIParams(params.rateParam, requestPeriods);

    if (params.view === 'table' && isCompareActive) {
      mapCompareParamsToAPIParams(params, requestPeriods);

      // We only need the rates of the current month in the Rates Table (unlike in the Rates Calendar)
      // Restricting the date range was originally needed to match the median calculated in frontend and backend:
      // backend calculated the median based on the total range which possibly included days of the previous and next month while frontend didn't
      requestDays['from_date_range_start'] = month
        .startOf('month')
        .format('YYYY-MM-DD');
      requestDays['from_date_range_end'] = month
        .endOf('month')
        .format('YYYY-MM-DD');
    }

    if (params.platform > PLATFORMS.PLATFORM_DESKTOP) {
      requestPeriods['allow_labels'] = ['no_label', 'mobile_only'];
    }

    // Add requestDays properties to requestPeriods, because both have the same base similarities
    // @ts-expect-error: Incorrect type
    extendObj(requestPeriods, requestDays);

    // Add some specifics to requestDays now that the extension is complete
    // We get demand change dates, which are set intervals calculated relative to today
    if (params.view === 'table' && params.compareType === 'extract_date') {
      // Only request the reference date when using the rates compare feature for dates
      requestDays['change_dates'] = [params.compareValue];
    } else if (this.access.hasFeature('pms.otb.view')) {
      // Request the compare period for in the tooltip based on the current month
      // For the current and next month show changes vs. 1 day ago, for all future ones vs. 7 days ago and no compare for past months
      // Because the calendar can contains days from the previous and next month we need to request those changes as well
      requestDays['change_dates'] = changeDates;
    }

    // Prepare promises for RSVP hashing
    const periodsRequest = this.store.query<Period>('period', requestPeriods);
    let occupancyRequest;

    // Prepare occupancies API call
    if (this.access.hasFeature('pms.otb.view')) {
      const occupancyParams: Record<string, string | string[] | undefined> = {
        namespace: APIGATEWAY,
        typeKey: 'revenueinsight/occupancies',
        subscription_id: requestDays['hotel_id'],
        from_date_range_start: requestDays['from_date_range_start'],
        from_date_range_end: requestDays['from_date_range_end'],
      };
      const changeDatesValue = requestDays['change_dates'];
      if (Array.isArray(changeDatesValue)) {
        changeDatesValue.forEach((changeDate, index) => {
          occupancyParams[`compare${index > 0 ? index + 1 : ''}_extract_date`] =
            changeDate;
        });
      }
      occupancyRequest = this.store.query<Occupancy>(
        'occupancy',
        occupancyParams,
      );
    }

    // let daysRequest;

    // Only request days if not already present in store
    // end - start = amount of days in range, +1 for correction, because if start === end, then it's 1 day long
    // If not equal, the selection for the date was changed, and new days should be loaded
    /*
    if (storeDays.length !== (endUnix - startUnix) + 1) {
      // Important query on days, which loads demands, holidays, occupancies, etc...
      daysRequest = this.store.query('day', requestDays);
    }
    */

    // Note for the requestDays checks above: can't handle the unloading of store data we've added recently (https://otainsight.atlassian.net/browse/SH-11466)
    // Always to daysRequest in this route for now (see below)

    // Staff users with no Rate Insight or Events can request Rate Insight but still lack the Events feature flag.
    let acceptedEventsRequest;
    let holidaysRequest;
    if (this.access.hasFeature('app.events')) {
      acceptedEventsRequest = this.store.query<Event>('event', {
        ...requestDays,
        typeKey: 'events/accepted',
        subscription_id: requestDays['hotel_id'],
      });

      holidaysRequest = this.store.query<Holiday>('holiday', {
        ...requestDays,
        typeKey: 'holidays',
        subscription_id: requestDays['hotel_id'],
      });
    }

    let rentalRequest;
    if (
      this.access.hasShortTermRentalAccess &&
      !this.access.hasShortTermRentalOnlyStrTab
    ) {
      if (isCompareActive && params.compareType === COMPARETYPES.DATE) {
        compareExtractDates['compare_extract_date'] = params.compareValue;
      }

      rentalRequest = this.store.query<ShortTermRentalRate>(
        'short-term-rental-rate',
        {
          ...requestDays,
          ...compareExtractDates,
          typeKey: 'shorttermrental/fromdates',
          use_filter: 'default',
        },
      );
    }

    let demandRequest;
    if (this.access.hasFeature('subscription.demand')) {
      requestDays['typeKey'] = this.access.hasMarketInsight
        ? 'demand/di/demands'
        : 'demand/ari/demands';

      // Replace deprecated query params from shared QP object
      const requestDaysForDemand = { ...requestDays };
      requestDaysForDemand['subscription_id'] = requestDays['hotel_id']!;
      delete requestDaysForDemand['hotel_id'];

      demandRequest = this.store.query<Demand>('demand', requestDaysForDemand);
    }

    // Perform roomtype request separately and only on:
    // 1. Initial load (or tab switch)
    // 2. If OTA id changes
    if (!this.lastRoomtypeResponse || this.requestedOtaId !== params.otaId) {
      this.requestedOtaId = params.otaId;
      // Blocking for roomtype filter and roomtype mapping modal
      this.store.unloadAll('roomtype');
      void this.store
        .query<Roomtype>('roomtype', {
          namespace: APIGATEWAY,
          hotel_id: hotelId,
          ota: params.otaId,
          typeKey: 'roomtypes/rates',
          data_type: 'rates',
        })
        .then((roomtypeResponse) => {
          // In case the own hotelinfo has no roomtypes (e.g. pre-opening hotel), fetch the roomtypes of the competitors instead
          // Check if both the roomtypes and the roomtypeSelection aren't empty (https://otainsight.slack.com/archives/C015VTDMZ4G/p1655129967713789)
          if (
            !roomtypeResponse.length ||
            !roomtypeResponse[0]?.roomtypeSelections.length
          ) {
            void this.store
              .query<Roomtype>('roomtype', {
                namespace: APIGATEWAY,
                hotel_id: hotelId,
                compset_ids: this.hotelAndMarketService.hotel?.sortedCompsets
                  .map((compset) => compset.apiId)
                  .join(','),
                ota: params.otaId,
                typeKey: 'roomtypes/mapping/rates',
                data_type: 'rates',
              })
              .then((roomtypemappingResponse) => {
                set(model.roomtypes, 'data', [
                  ...model.roomtypes.data,
                  ...roomtypemappingResponse,
                ]);
                this.lastRoomtypeResponse = roomtypemappingResponse;
                set(model, 'roomtypes.isLoading', false);
              });
          } else {
            set(model.roomtypes, 'data', [
              ...model.roomtypes.data,
              ...roomtypeResponse,
            ]);
            this.lastRoomtypeResponse = roomtypeResponse;
            set(model, 'roomtypes.isLoading', false);
          }
        });
    } else {
      set(model.roomtypes, 'data', [
        ...model.roomtypes.data,
        ...this.lastRoomtypeResponse,
      ]);
      set(model, 'roomtypes.isLoading', false);
    }

    // Perform all other requests
    // Blocking for entire Rates tab except roomtype filter and roomtype mapping modal
    void hash({
      periodsResult: periodsRequest,
      demandResult: demandRequest,
      occupanciesResult: occupancyRequest,
      acceptedEventsResult: acceptedEventsRequest,
      holidaysResult: holidaysRequest,
      rentalResult: rentalRequest,
    }).then(async (resolvedPromises) => {
      const { periodsResult, rentalResult } = resolvedPromises;
      const periodsResultMeta =
        periodsResult.meta as unknown as PeriodsResponseMeta;
      const rentalResultMeta = rentalResult?.meta as
        | RentalResponseMeta
        | undefined;

      if (rentalResultMeta) {
        const { relevant_properties: relevantProperties } = rentalResultMeta;
        this.rates.relevantRentalProperties = relevantProperties;
      }

      // Clear the dummy periods from the model again #DUMMYPERIOD (Step 2)
      set(model, 'periods.data', [...periodsResult]);

      // Update disconnected OTAs - refresh when a new ota gets disconnected
      refreshIfOtaIsDisconnected(
        periodsResultMeta.disconnected_otas,
        this.store,
        this.alerting,
      );

      // Shopping reduction - do a liveshop if the hotel has rates shopping paused
      try {
        await this.shoppingReductionLiveShopCheck(
          // @ts-expect-error: Incorrect type
          requestDays['hotel_id'],
          periodsResult,
          dateRangeStart,
          dateRangeEnd,
          params.otaId,
        );
      } finally {
        set(model, 'periods.isLoading', false);
      }
    });

    // Make sure that the liveupdate service knows what is selected
    // This is important so we can show the spinner if a liveupdate is active for the selection
    this.liveupdate.updateSelection({
      currentHotelId: hotelId,
      selectedOTA: params.otaId,
      los: params.los,
      month: params.month,
      mealtype: params.mealPlan,
      maxPersons: params.maxPersons,
      roomtype: params.roomType,
      rateParam: params.rateParam,
      membershipType: params.membershipType,
      platform: params.platform,
      rateChanges: params.rateChanges,
      // @ts-expect-error: Incorrect type
      compareType: params.compareType,
      compareValue: params.compareValue,
      compsetId: params.compsetId,
    });

    // Store last viewed channel in cookie so we can use it as default later
    if (params.otaId) {
      // @ts-expect-error: setCookie accepts objects that get serialized by js-cookie
      this.cookie.setCookie(HOTEL_COOKIE_NAME, {
        id: hotelId,
        type: 'hotel',
        otaId: params.otaId,
      });
    }

    // Push a dummy period object for every day in range to have a clean loading state while periods request is loading #DUMMYPERIOD (Step 1)
    getPeriodsInRange(dateRangeStart, dateRangeEnd, 'day').forEach(() =>
      model.periods.data.push({}),
    );

    return model;
  }

  async shoppingReductionLiveShopCheck(
    subscriptionId: string,
    periodsResult: Period[],
    dateRangeStart: Dayjs,
    dateRangeEnd: Dayjs,
    ota: string,
  ): Promise<void> {
    // Don't do an automatic liveshop if the dateRangeStart is more then 90 days in the future
    if (
      !this.access.hasRatesShoppingPaused ||
      dateRangeStart.isAfter(
        this.hotelAndMarketService
          .getLocalTime()
          .startOf('day')
          .add(MAX_FUTURE_INTERNAL_LIVESHOP_DAYS, 'day'),
      ) ||
      (this.liveupdate.activeLiveupdatesForThisSelection?.length ?? 0) > 0
    ) {
      return;
    }

    const otasFromPeriods = periodsResult[0]!.ownHotelRates.map(
      (rate) => rate.ota,
    );

    const extractDateTimesPerOta: Record<string, Dayjs[]> = {};
    const outdatedOtas: string[] = [];

    // We first get all extract dates for the OTAs in the periodsResult
    // If the OTA is ALL_CHANNELS, we need to get all extract dates for all OTAs
    const otasToCheck = ota === OTA_IDS.ALL_CHANNELS ? otasFromPeriods : [ota];

    otasToCheck.forEach((otaId) => {
      periodsResult.forEach((period) => {
        const extractDateTime =
          period.getOwnHotelRateForOta(otaId)?.extract_datetime;
        if (extractDateTime) {
          extractDateTimesPerOta[otaId] = extractDateTimesPerOta[otaId] || [];
          extractDateTimesPerOta[otaId].push(dayjs(extractDateTime));
        }
      });
    });

    // Check if the latest extract date is older than 1 day
    // If the latest extract date is older than 1 day, we need to do a liveshop
    Object.keys(extractDateTimesPerOta).forEach((otaId) => {
      const extractDatesForOta = extractDateTimesPerOta[otaId];
      if (extractDatesForOta && extractDatesForOta.length > 0) {
        const latestExtractDateTime = dayjs.max(extractDatesForOta);
        const hasRecentExtract = latestExtractDateTime?.isAfter(
          dayjs.utc().subtract(1, 'day'),
        );
        if (!hasRecentExtract) {
          outdatedOtas.push(otaId);
        }
      }
    });

    if (outdatedOtas.length > 0) {
      const shoppingReductionsResponse =
        await this.fetchScheduledShopPausesTask.perform({
          subscriptionId,
        });

      outdatedOtas.forEach((otaId) => {
        // If the OTA is in the list of paused OTAs, we need to do a liveshop
        if (
          shoppingReductionsResponse?.some(
            (shoppingReduction) => shoppingReduction.ota === otaId,
          )
        ) {
          const formattedDateRangeStart = dateRangeStart.format('YYYY-MM-DD');
          const formattedDateRangeEnd = dateRangeEnd.format('YYYY-MM-DD');
          this.liveupdate.startLiveRateShop(
            false,
            formattedDateRangeStart,
            formattedDateRangeEnd,
            null,
            true,
            otaId,
          );
          this.usageAnalytics.track('Internal Shop Triggered', {
            eventTrigger: 'internal_shop_triggered',
            channel: otaId,
            dateRangeStart: formattedDateRangeStart,
            dateRangeEnd: formattedDateRangeEnd,
          });
        }
      });
    }
  }

  @action
  doRefresh(): void {
    this.refresh();
  }
}
